import React from 'react';
import { Link } from 'react-router-dom';
import { Twitter, Youtube, BookOpen, Radio, Globe } from 'lucide-react';
import FollowButton from './FollowButton';

const PLATFORM_ICONS = {
  twitter: Twitter,
  youtube: Youtube,
  substack: BookOpen,
  podcast: Radio,
};

function ReturnBadge({ value }) {
  if (value == null) return <span className="font-semibold text-gray-500">N/A</span>;
  const n = parseFloat(value);
  if (n === 0) return <span className="font-semibold text-gray-500">N/A</span>;
  const color = n > 0 ? 'text-emerald-600' : 'text-red-600';
  const sign = n >= 0 ? '+' : '';
  return (
    <span className={`font-semibold ${color}`}>
      {sign}{n.toFixed(1)}%
    </span>
  );
}

export default function AnalystCard({ analyst, noLink = false, onCardClick, showFollow = false, onAuthRequired, returnPeriod = 'all', periodField = {} }) {
  const returnFieldKey = periodField[returnPeriod] ?? 'overall_return_pct';
  const returnValue = analyst[returnFieldKey];
  const returnLabel = returnPeriod === 'all' ? 'Avg Return' : `${returnPeriod.toUpperCase()} Return`;
  return (
    <Link
      to={`/analysts/${analyst.slug}`}
      onClick={noLink ? (e) => { e.preventDefault(); onCardClick?.(); } : undefined}
      className={`relative flex flex-col bg-white rounded-xl border border-gray-100 p-4 transition-all duration-200 ${
        noLink ? 'cursor-pointer hover:border-gray-300 hover:shadow-md' : 'hover:border-gray-300 hover:shadow-md'
      }`}
    >
      {/* Avatar + name */}
      <div className="flex items-start gap-3 mb-3">
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-lg font-bold text-gray-600 flex-shrink-0 overflow-hidden">
          {analyst.profile_image_url
            ? <img src={analyst.profile_image_url} alt={analyst.name} className="w-full h-full object-cover" />
            : analyst.name?.[0]}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">{analyst.name}</h3>
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {(analyst.focus_countries ?? []).slice(0, 2).map((c) => (
              <span key={c} className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{c}</span>
            ))}
            {(analyst.focus_asset_classes ?? []).slice(0, 2).map((a) => (
              <span key={a} className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">{a}</span>
            ))}
          </div>
        </div>

        {/* Heart follow button — authenticated leaderboard only */}
        {showFollow && (
          <FollowButton
            slug={analyst.slug}
            initialFollowing={analyst.is_following ?? false}
            onAuthRequired={onAuthRequired}
          />
        )}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-2 text-center pt-3 border-t border-gray-50">
        <div>
          <div className="text-xs text-gray-400 mb-0.5">Predictions</div>
          <div className="font-semibold text-gray-800 text-sm">{analyst.predictions_count}</div>
        </div>
        <div>
          <div className="text-xs text-gray-400 mb-0.5">{returnLabel}</div>
          <div className="text-sm" title={returnValue == null && returnPeriod !== 'all' ? 'Not enough data for this period' : undefined}>
            <ReturnBadge value={returnValue} />
          </div>
        </div>
      </div>
    </Link>
  );
}
